<script setup lang="ts">
import type { Query } from '@directus/types'

const theTradeshowPage = useState<DefaultTradeshow | undefined>('tradeshow-page')
definePageMeta({
  title: 'Tradeshows',
  description: 'Find ICS and the rest of the Trexon family at these upcoming tradeshows and events.',
  order: 1,
})

const route = useRoute()

const query: Query = {
  filter: {
    status: { _eq: 'published' },
  },
  sort: ['start_date'],
}

const { data: tradeshows } = await useFetch<DefaultTradeshow[]>(
  '/api/items/tradeshows',
  {
    key: 'page:tradeshows:all-tradeshows',
    query,
    getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key],
  },
)

const atTradeshowsRoot = computed(() => /\/about\/tradeshows\/?$/.test(route.path))

const headline = computed(() => atTradeshowsRoot.value ? 'Upcoming Tradeshows' : theTradeshowPage.value?.name)
</script>

<template>
  <BlockContent
    element="section"
    class="dark:text-icssilver-50"
  >
    <h1 class="subpage-title mb-12">
      {{ headline }}
    </h1>
    <template v-if="atTradeshowsRoot">
      <div
        v-if="tradeshows && tradeshows.length > 0"
        class="prose sm:text-lg"
      >
        <p>
          Find ICS and the rest of the Trexon family at these upcoming events.
        </p>
        <p class="mb-12">
          Going to a show? <NuxtLink
            to="/contact"
            class="link inline-block"
          >
            Let us know you're stopping by.
          </NuxtLink>
        </p>
      </div>
      <div
        v-else
        class="text-lg py-6"
      >
        <p>There are no upcoming tradeshows at this time.</p>
      </div>
      <div
        v-if="tradeshows && tradeshows.length > 0"
        class="gap-8 grid auto-rows-min sm:grid-cols-[max-content_minmax(auto,max-content)_auto]"
      >
        <ATradeshowContext
          v-for="tradeshow in tradeshows"
          :key="tradeshow.id"
          :tradeshow
        >
          <template #default="{ startDate, endDate, to }">
            <div class="grid grid-rows-subgrid sm:row-span-2">
              <div class="flex flex-col justify-center">
                <p class="text-lg text-icsred">
                  {{ startDate }}
                </p>
                <p class="text-sm text-icsgray-800 dark:text-icsgray-300">
                  to {{ endDate }}
                </p>
              </div>
            </div>
            <NuxtLink
              :to="to"
              class="group gap-y-4 grid w-fit sm:col-span-1 sm:row-span-2 sm:grid-rows-subgrid"
            >
              <h2 class="text-3xl heading-link-bright flex capitalize items-center font-flex-600-120">
                {{ tradeshow.name }}
              </h2>
              <NuxtImg
                v-if="tradeshow.logo"
                :src="tradeshow.logo"
                :alt="`The ${tradeshow.name} logo.`"
                class="rounded-xs object-contain f-w-240-300 dark:p-2 dark:bg-icsgray-100"
              />
            </NuxtLink>
            <div class="sm:row-span-2">
              <ul>
                <li>
                  {{ tradeshow.city }}<span v-if="tradeshow.state !== 'Other'">, {{ tradeshow.state }}</span>
                </li>
                <li v-if="tradeshow.venue">
                  {{ tradeshow.venue }}
                </li>
                <li v-if="tradeshow.booth">
                  <span class="text-xs uppercase">Booth:</span> {{ tradeshow.booth }}
                </li>
              </ul>
              <div class="mt-4 flex flex-wrap gap-x-4 gap-y-1">
                <NuxtLink
                  :to="to"
                  class="text-sm link inline-flex gap-1"
                >
                  More Details
                </NuxtLink>
                <NuxtLink
                  :to="tradeshow.url"
                  class="text-sm link inline-flex gap-1"
                >
                  <div>Visit Show Site</div> <div class="i-carbon-launch translate-y-px scale-90" />
                </NuxtLink>
              </div>
            </div>
            <hr class="b-icsgray dark:b-icsnight-800 sm:col-span-3 last:hidden">
          </template>
        </ATradeshowContext>
      </div>
    </template>
    <NuxtPage v-else />
  </BlockContent>
</template>
